/**
 * Loan details — the terms that pick which rows of the sheet price the deal.
 *
 * Laid out the way the RTL sizer lays out its own Loan details card: the
 * HIGH-SIGNAL choices are chip groups on their own lines (a dropdown hides two
 * thirds of the decision space behind a click), the amounts sit in an even grid
 * beneath them, and the counterparty goes LAST and full width because it scopes
 * the whole scenario rather than describing the loan.
 */
import {
  Section,
  Grid,
  Field2,
  ChipGroup,
  DelayedPurchaseChips,
  MoneyInput,
  PercentInput,
  SelectInput,
} from "../../sizersShared/sizerUi";
import SizerSelectAutocomplete from "../../sizersShared/SizerSelectAutocomplete";
import {
  AMORTIZATIONS,
  INT_TYPES,
  PPPS,
  PURPOSES,
  TIERS,
} from "../dscrConstants";
import {
  DELAYED_PURCHASE_HINT,
  DELAYED_PURCHASE_LABEL,
  canBeDelayedPurchase,
} from "../../../utils/loans/delayedPurchase";

/**
 * No `GateError` on this card by design: the endpoint's REQUIRED_BY_LLPA gates
 * name `resolvedCreditScore`, the rent basis and `address.zip` — all of which
 * live on the Borrower, Income and Property cards. Nothing here is gated.
 */
export default function LoanDetailsSection({ form, set, correspondents = [] }) {
  const correspondentOptions = [
    { value: "", label: "Eastview base (no correspondent)" },
    ...correspondents,
  ];

  return (
    <Section
      first
      title="Loan details"
      subtitle="These choices select which rows of the pricing sheet apply."
    >
      {/* Purpose and rate structure share a row split 7/5 — purpose carries the
          long labels ("Refinance - Cash Out") and the ladder columns are three
          short ones, so an even split would wrap purpose while leaving the
          other half empty. Same treatment as the RTL card's Purpose/Term row. */}
      <Grid cols={12}>
        <Field2 label="Purpose" span={7} required>
          <ChipGroup
            sm
            value={form.loanPurpose}
            onChange={(v) => {
              set("loanPurpose", v);
              // The flag only applies on a cash-out refi — clear it elsewhere.
              if (!canBeDelayedPurchase(v)) set("delayedPurchase", null);
            }}
            options={PURPOSES}
          />
        </Field2>
        <Field2
          label="Rate structure"
          span={5}
          tip="The coupon-ladder column on the pricing sheet — fixed or the ARM's fixed period."
        >
          <ChipGroup
            sm
            value={form.armProduct}
            onChange={(v) => set("armProduct", v)}
            options={INT_TYPES}
          />
        </Field2>
      </Grid>

      {/* REQ-106: delayed purchase is a boolean on a cash-out refi, not a
          purpose. Required while it shows. */}
      {canBeDelayedPurchase(form.loanPurpose) && (
        <Grid cols={1} sx={{ mt: "24px" }}>
          <Field2 label={DELAYED_PURCHASE_LABEL} required hint={DELAYED_PURCHASE_HINT}>
            <DelayedPurchaseChips
              value={form.delayedPurchase}
              onChange={(v) => set("delayedPurchase", v)}
            />
          </Field2>
        </Grid>
      )}

      {/* The canonical loan dropdown — `amortizing` is DERIVED from it
          server-side, and it is what reaches the sheet's Interest Only LLPA
          row. Chips because there are three and the choice moves the payment. */}
      <Grid cols={1} sx={{ mt: "24px" }}>
        <Field2 label="Amortization" required>
          <ChipGroup
            sm
            value={form.amortizationType}
            onChange={(v) => set("amortizationType", v)}
            options={AMORTIZATIONS}
          />
        </Field2>
      </Grid>

      {/* THE RATE THE USER IS ASKING FOR sits beside the amount because the two
          together are the question: "what does this loan at this rate do?" Sent,
          the Result block anchors to the nearest rung — how THIS rate prices —
          and the ladder still prices every rung above and below it. Left empty,
          the answer anchors at best execution instead. */}
      <Grid cols={2} sx={{ mt: "26px" }}>
        <Field2 label="Loan amount" required>
          <MoneyInput
            value={form.requestedLoanAmount}
            onChange={(v) => set("requestedLoanAmount", v)}
          />
        </Field2>
        <Field2
          label="Requested rate"
          hint="Leave blank to anchor the answer at best execution — the highest-premium passing rung."
        >
          <PercentInput
            value={form.requestedCoupon}
            onChange={(v) => set("requestedCoupon", v)}
            factor={100}
            placeholder="7.250"
          />
        </Field2>
      </Grid>

      <Grid cols={1} sx={{ mt: "26px" }}>
        <Field2
          label="Prepay penalty"
          tip="Leave unset to price the sheet's own No Prepayment Penalty row and its max-price clamp — a real pricing outcome, not a missing input."
        >
          <SelectInput
            value={form.prepaymentPenaltyStructure}
            onChange={(v) => set("prepaymentPenaltyStructure", v)}
            options={PPPS}
            placeholder="No prepayment penalty"
          />
        </Field2>
      </Grid>

      {/* Who the deal is priced FOR — the only inputs on this card that are not
          properties of the loan, so they close it out on one row of their own. */}
      <Grid cols={12} sx={{ mt: "26px" }}>
        {/* Type-or-browse, not a menu: this list grows with every counterparty
            onboarded, and a plain dropdown makes finding one a scroll. */}
        <Field2
          label="Correspondent"
          span={6}
          tip="Resolves the account margin exactly the way the rate lock does; shown back as marginPointsApplied."
        >
          <SizerSelectAutocomplete
            value={form.correspondentId ?? ""}
            onChange={(v) => set("correspondentId", v || null)}
            options={correspondentOptions}
            placeholder="Search correspondents"
            noOptionsText="No correspondent matches"
          />
        </Field2>
        {/* No pricing-sheet picker: the server prices the platform sheet and the
            result line names it (owner 2026-10-08). */}
        <Field2 label="Tier" span={6}>
          <SelectInput
            value={form.tier ?? ""}
            onChange={(v) => set("tier", v)}
            options={TIERS}
          />
        </Field2>
      </Grid>
    </Section>
  );
}
